import React, {
  useMemo,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import isNil from 'lodash/isNil';
import {
  EncounterPatientWriteOffDTO,
} from 'dtos';
import {
  useFieldArray,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Button from 'pages/Dashboard/components/Button';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Select from 'core/components/Select';
import {
  useUpdateWriteOff,
  useWriteOffOptions,
} from 'pages/Dashboard/pages/Billing/hooks';
import Tooltip from 'core/components/Tooltip';
import head from 'lodash/head';
import {
  serializeWriteOffs,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  appointmentsEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import clsx from 'clsx';

type WriteOffsForm = {
  data: Partial<EncounterPatientWriteOffDTO>[];
};

const defaultWriteOffValues: Partial<EncounterPatientWriteOffDTO> = Object.freeze({
  reason: null,
  writeOffAmount: 0,
});

function validateWriteOffAmount(value: unknown) {
  const amount = Number(value);
  return !isNil(value) && Number.isFinite(amount) && amount !== 0;
}

type Part = 'root' | 'add';

type Props = {
  onUpdate?: () => void;
  onCancel?: () => void;
  onChange?: (value: EncounterPatientWriteOffDTO[]) => void;
  value?: EncounterPatientWriteOffDTO[];
  classes?: Partial<Record<Part, string>>;
};

export default function WriteOffForm({
  onUpdate,
  onCancel,
  onChange,
  value: initialValue = [],
  classes,
}: Props) {
  const encounterId = useMemo(() => head(initialValue ?? [])?.encounterId ?? 0, [initialValue]);
  const defaultFormValues = useMemo(() => ({
    data: initialValue.map((writeOff) => ({
      ...defaultWriteOffValues,
      ...writeOff,
    })),
  }), [initialValue]);
  const defaultNewWriteOffValues = useMemo(() => ({
    ...defaultWriteOffValues,
    encounterId,
  }), [encounterId]);

  const {
    control,
    trigger,
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = useForm<WriteOffsForm>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
    shouldUnregister: true,
  });
  const {
    append,
    remove,
    fields,
  } = useFieldArray({ control, name: 'data' });
  const options = useWriteOffOptions();

  const handleAppend = async () => {
    const isValid = await trigger('data', { shouldFocus: true });
    if (isValid) {
      append(defaultNewWriteOffValues);
    }
  };
  const { mutateAsync: updateWriteOff } = useUpdateWriteOff();

  const handleUpdate = async (formData: WriteOffsForm) => {
    try {
      const payload = serializeWriteOffs(formData?.data, encounterId);
      await updateWriteOff(payload);
      paymentsEventChannel.emit('onPatientBalanceChange');
      appointmentsEventChannel.emit('onEventUpdated', encounterId);
      onUpdate?.();
    } catch (error) {
      console.error(error);
    }
  };

  const buttonText = useMemo(() => getSubmitButtonText(false, isSubmitting), [isSubmitting]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col populate-form gap-form-row">
        <div className="w-full flex flex-col max-h-[320px] overflow-auto gap-form-row p-1">
          {fields.map((item, index) => (
            <div
              key={item.id}
              className="flex w-full items-end gap-form-field text-xs"
            >
              <Controller
                control={control}
                name={`data.${index}.writeOffAmount`}
                rules={{
                  required: true,
                  validate: validateWriteOffAmount,
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="payment-writeoff-value"
                    label={index === 0 ? 'Write Off Amount' : undefined}
                    className="w-32"
                    error={invalid}
                    showErrorText={false}
                    required
                  >
                    <CurrencyTextField
                      value={typeof field.value === 'number' || typeof field.value === 'string' || isNil(field.value)
                        ? field.value ?? null
                        : null}
                      onChange={field.onChange}
                      error={invalid}
                      showErrorText={false}
                      allowNegative
                      onBlur={(e) => {
                        onChange?.([
                          ...initialValue.slice(0, index),
                          {
                            ...initialValue[index],
                            writeOffAmount: Number(field.value),
                          },
                          ...initialValue.slice(index + 1),
                        ]);
                        field.onBlur();
                      }}
                    />
                  </Wrapper>
                )}
              />
              <Controller
                control={control}
                name={`data.${index}.reason`}
                rules={{ required: true }}
                render={({ field, fieldState: { invalid } }) => (
                  <Select<string>
                    {...field}
                    label={index === 0 ? 'Reason' : undefined}
                    required
                    className="w-64"
                    error={invalid}
                    showErrorText={false}
                    options={options}
                    placeholder="Select Reason"
                    onChange={(option) => {
                      onChange?.([
                        ...initialValue.slice(0, index),
                        {
                          ...initialValue[index],
                          reason: option as string,
                        },
                        ...initialValue.slice(index + 1),
                      ]);
                      field.onChange(option);
                    }}
                    getValue={(option) => option}
                    getLabel={(option) => option}
                  />
                )}
              />
              <div>
                <Tooltip title="Delete">
                  <IconButton
                    className="flex-auto"
                    onClick={() => {
                      remove(index);
                      onChange?.([
                        ...initialValue.slice(0, index),
                        ...initialValue.slice(index + 1),
                      ]);
                    }}
                  >
                    <ClearIcon className="text-gray-400 !text-lg" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
        <div className="w-full">
          <Button
            className={clsx(classes?.add)}
            onClick={handleAppend}
            variant="outlined"
            color="primary"
          >
            <AddIcon
              className="mr-1"
              fontSize="small"
            />
            Add
          </Button>
        </div>
      </div>
      <div className="flex gap-2 w-full">
        <Button
          className="!block w-1/2 !max-w-[initial]"
          variant="outlined"
          onClick={onCancel}
        >
          Close
        </Button>
        <Button
          className="!block w-1/2 !max-w-[initial]"
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleUpdate)}
          disabled={!isValid || isSubmitting}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
}
